import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut, BookOpen, Trophy, Target, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ProfilePage() {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [progress, setProgress] = useState(null);

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    const load = async () => {
      try {
        const [sRes, pRes] = await Promise.all([
          axios.get(`${API}/stats`),
          axios.get(`${API}/progress`, { withCredentials: true }),
        ]);
        setStats(sRes.data);
        setProgress(pRes.data);
      } catch (e) { console.error(e); }
    };
    load();
  }, [user, loading, navigate]);

  if (loading) {
    return (
      <div className="pt-24 pb-16 min-h-screen flex items-center justify-center" data-testid="profile-loading">
        <div className="animate-pulse text-nordic-text-muted">Loading...</div>
      </div>
    );
  }
  if (!user) return null;

  const totalWords = stats?.total_words || 0;
  const learnedCount = progress?.total_learned || 0;
  const progressPct = totalWords > 0 ? Math.round((learnedCount / totalWords) * 100) : 0;
  const quizScores = progress?.quiz_scores || [];
  const avgScore = quizScores.length > 0
    ? Math.round(quizScores.reduce((a, s) => a + (s.score / s.total) * 100, 0) / quizScores.length)
    : 0;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="pt-24 pb-16 min-h-screen" data-testid="profile-page">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          {/* User Info */}
          <div className="flex items-center gap-5 mb-10">
            {user.picture ? (
              <img src={user.picture} alt={user.name} className="w-16 h-16 rounded-full border-2 border-nordic-wood/20" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-nordic-blue/10 flex items-center justify-center">
                <span className="font-heading text-2xl text-nordic-blue">{user.name?.[0]?.toUpperCase()}</span>
              </div>
            )}
            <div>
              <h1 className="font-heading text-3xl font-medium text-nordic-text" data-testid="profile-name">{user.name}</h1>
              <p className="text-nordic-text-secondary text-sm" data-testid="profile-email">{user.email}</p>
            </div>
            <Button
              variant="outline"
              onClick={handleLogout}
              className="ml-auto rounded-full border-nordic-wood/30"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" /> Log Out
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-10">
            <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 p-6" data-testid="stat-words-learned">
              <BookOpen className="w-8 h-8 text-nordic-blue mb-3" />
              <div className="font-heading text-3xl font-medium text-nordic-text">{learnedCount}</div>
              <p className="text-sm text-nordic-text-secondary">Words Learned</p>
            </div>
            <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 p-6" data-testid="stat-quiz-avg">
              <Trophy className="w-8 h-8 text-nordic-accent mb-3" />
              <div className="font-heading text-3xl font-medium text-nordic-text">{avgScore}%</div>
              <p className="text-sm text-nordic-text-secondary">Avg Quiz Score</p>
            </div>
            <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 p-6" data-testid="stat-total-available">
              <Target className="w-8 h-8 text-nordic-success mb-3" />
              <div className="font-heading text-3xl font-medium text-nordic-text">{totalWords}</div>
              <p className="text-sm text-nordic-text-secondary">Total Words Available</p>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 p-8 mb-10" data-testid="overall-progress">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading text-lg text-nordic-text font-medium">Overall Progress</h3>
              <span className="text-sm text-nordic-text-muted">{progressPct}% complete</span>
            </div>
            <Progress value={progressPct} className="h-3 bg-nordic-wood/20" />
            <p className="text-sm text-nordic-text-secondary mt-3">
              {learnedCount} of {totalWords} words learned
            </p>
          </div>

          {/* Recent Quiz Scores */}
          {quizScores.length > 0 && (
            <div className="bg-nordic-paper rounded-2xl border border-nordic-wood/10 p-8" data-testid="quiz-history">
              <h3 className="font-heading text-lg text-nordic-text font-medium mb-4 flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-nordic-blue" /> Recent Quizzes
              </h3>
              <div className="space-y-3">
                {quizScores.slice(-10).reverse().map((s, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-nordic-wood/10 last:border-0">
                    <span className="text-sm text-nordic-text capitalize">{s.category || "All categories"}</span>
                    <span className="text-sm font-medium text-nordic-blue">{s.score}/{s.total}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
